import { Collapse } from '@sgme/ui';
import classNames from 'classnames';
import { OpenModalButton } from 'components/shared/OpenInModal/OpenModalButton';
import { IntlKey } from 'locales';
import { PropsWithChildren, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useIntl } from 'react-intl';

export type FormatSectionTitleFn<K extends string> = (section: K, formatMessage: (key: IntlKey) => string) => string;

type AccordionSectionProps<K extends string> = {
  section: K;
  showSpinners: boolean;
  disabled?: boolean;
  openModal?: () => void;
  buttonClassName?: string;
  e2e?: string;
  getTitle: FormatSectionTitleFn<K>;
};

export function AccordionSection<K extends string>({
  section,
  buttonClassName,
  showSpinners,
  openModal,
  disabled,
  getTitle,
  e2e,
  children,
}: PropsWithChildren<AccordionSectionProps<K>>) {
  const { formatMessage } = useIntl();
  const title = useMemo(
    () => getTitle(section, k => formatMessage({ id: k, defaultMessage: section })),
    [getTitle, section, formatMessage],
  );

  return (
    <Collapse.Item itemKey={section} className="card bg-lvl2 mb-2 my-2">
      <Collapse.Header className={classNames('btn w-100 ps-3 py-2 d-flex', buttonClassName)} disabled={disabled}>
        <div className="d-flex justify-content-between align-items-center w-100" data-e2e={e2e}>
          <span className="fs-5">{title}</span>
          {showSpinners ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            openModal !== undefined && <OpenModalButton openModal={openModal} />
          )}
        </div>
      </Collapse.Header>
      <Collapse.Body>{children}</Collapse.Body>
    </Collapse.Item>
  );
}
